/**
 * ListFormModal Component
 * Modal for creating and editing lists with QueryBuilder support
 */

import { useState, useEffect } from "react";
import type {
  List,
  CreateListInput,
  UpdateListInput,
  QueryCriteria,
  ListTriggerType,
  TriggerConfig,
} from "../../types/list.types";
import type { UUID } from "../../types/common.types";
import QueryBuilder from "./QueryBuilder";
import TriggerConfigSection from "./TriggerConfigSection";
import { previewListQueryCount } from "../../services/lists.service";
import TagAutocompleteInput from "../common/TagAutocompleteInput";

interface ListFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (input: CreateListInput | UpdateListInput) => Promise<void>;
  list?: List | null; // If provided, we're editing
  title?: string;
}

const defaultQueryCriteria: QueryCriteria = {
  operator: "AND",
  conditions: [],
};

export default function ListFormModal({
  isOpen,
  onClose,
  onSave,
  list,
  title,
}: ListFormModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDynamic, setIsDynamic] = useState(true);
  const [queryCriteria, setQueryCriteria] =
    useState<QueryCriteria>(defaultQueryCriteria);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Trigger configuration state (Phase 3)
  const [triggerType, setTriggerType] = useState<ListTriggerType>("NONE");
  const [triggerConfig, setTriggerConfig] = useState<TriggerConfig>({});
  const [autoGenerateVouchers, setAutoGenerateVouchers] = useState(false);
  const [linkedOfferId, setLinkedOfferId] = useState<UUID | null>(null);
  const [evaluationIntervalMinutes, setEvaluationIntervalMinutes] =
    useState(1440);
  const [isActive, setIsActive] = useState(true);
  const [entryTag, setEntryTag] = useState("");
  const [exitTag, setExitTag] = useState("");

  // Initialize form when editing
  useEffect(() => {
    if (list) {
      setName(list.name);
      setDescription(list.description || "");
      setIsDynamic(list.is_dynamic);
      setQueryCriteria(list.query_criteria || defaultQueryCriteria);
      // Trigger configuration
      setTriggerType(list.trigger_type || "NONE");
      setTriggerConfig(list.trigger_config || {});
      setAutoGenerateVouchers(list.auto_generate_vouchers || false);
      setLinkedOfferId(list.linked_offer_id || null);
      setEvaluationIntervalMinutes(list.evaluation_interval_minutes || 1440);
      setIsActive(list.is_active !== false);
      // Tag actions stored as extra props in trigger_config
      const tc = (list.trigger_config || {}) as Record<string, unknown>;
      setEntryTag((tc.entry_tag as string) || "");
      setExitTag((tc.exit_tag as string) || "");
    } else {
      // Reset for new list
      setName("");
      setDescription("");
      setIsDynamic(true);
      setQueryCriteria(defaultQueryCriteria);
      setEntryTag("");
      setExitTag("");
      // Reset trigger configuration
      setTriggerType("NONE");
      setTriggerConfig({});
      setAutoGenerateVouchers(false);
      setLinkedOfferId(null);
      setEvaluationIntervalMinutes(1440);
      setIsActive(true);
    }
    setPreviewCount(null);
    setErrors({});
  }, [list, isOpen]);

  const handlePreview = async () => {
    if (!isDynamic) {
      return;
    }

    setIsPreviewLoading(true);
    try {
      const count = await previewListQueryCount(queryCriteria);
      setPreviewCount(count);
    } catch (error) {
      console.error("Error previewing list:", error);
      setPreviewCount(null);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "Name is required";
    }

    if (
      isDynamic &&
      (!queryCriteria.conditions || queryCriteria.conditions.length === 0)
    ) {
      newErrors.criteria =
        "At least one condition is required for dynamic lists";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      const tagConfig = {
        ...(entryTag.trim() ? { entry_tag: entryTag.trim() } : {}),
        ...(exitTag.trim() ? { exit_tag: exitTag.trim() } : {}),
      };
      const hasTrigger = isDynamic && triggerType !== "NONE";
      const hasTagConfig = Object.keys(tagConfig).length > 0;

      const input: CreateListInput | UpdateListInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        is_dynamic: isDynamic,
        query_criteria: isDynamic ? queryCriteria : defaultQueryCriteria,
        // Trigger configuration (only for dynamic lists)
        trigger_type: isDynamic ? triggerType : "NONE",
        trigger_config:
          hasTrigger || hasTagConfig
            ? { ...(hasTrigger ? triggerConfig : {}), ...tagConfig }
            : undefined,
        auto_generate_vouchers: isDynamic ? autoGenerateVouchers : false,
        linked_offer_id:
          isDynamic && autoGenerateVouchers ? linkedOfferId : undefined,
        evaluation_interval_minutes: isDynamic
          ? evaluationIntervalMinutes
          : undefined,
        is_active: isActive,
      };

      await onSave(input);
      onClose();
    } catch (error) {
      console.error("Error saving list:", error);
      setErrors({ submit: "Failed to save list. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {title || (list ? "Edit List" : "Create List")}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Name Field */}
            <div>
              <label
                htmlFor="list-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="list-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter list name..."
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Description Field */}
            <div>
              <label
                htmlFor="list-description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                id="list-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter description (optional)..."
              />
            </div>

            {/* Type Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                List Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="list-type"
                    checked={isDynamic}
                    onChange={() => setIsDynamic(true)}
                    className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Dynamic
                    <span className="ml-1 text-xs text-gray-500">
                      (auto-updates based on rules)
                    </span>
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="list-type"
                    checked={!isDynamic}
                    onChange={() => setIsDynamic(false)}
                    className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Static
                    <span className="ml-1 text-xs text-gray-500">
                      (manually curated)
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* Query Builder (for dynamic lists) */}
            {isDynamic && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Criteria
                  </label>
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={isPreviewLoading}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded disabled:opacity-50 flex items-center gap-1"
                  >
                    {isPreviewLoading ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Loading...
                      </>
                    ) : (
                      "Preview"
                    )}
                  </button>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <QueryBuilder
                    group={queryCriteria}
                    onChange={setQueryCriteria}
                  />
                </div>

                {errors.criteria && (
                  <p className="mt-2 text-sm text-red-600">{errors.criteria}</p>
                )}

                {/* Preview Results */}
                {previewCount !== null && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">{previewCount}</span>{" "}
                      contact{previewCount !== 1 ? "s" : ""} match this criteria
                    </p>
                  </div>
                )}

                {/* Trigger Configuration (Phase 3) */}
                <TriggerConfigSection
                  triggerType={triggerType}
                  triggerConfig={triggerConfig}
                  autoGenerateVouchers={autoGenerateVouchers}
                  linkedOfferId={linkedOfferId}
                  evaluationIntervalMinutes={evaluationIntervalMinutes}
                  isActive={isActive}
                  onChange={(updates) => {
                    if (updates.trigger_type !== undefined) {
                      setTriggerType(updates.trigger_type);
                    }
                    if (updates.trigger_config !== undefined) {
                      setTriggerConfig(updates.trigger_config);
                    }
                    if (updates.auto_generate_vouchers !== undefined) {
                      setAutoGenerateVouchers(updates.auto_generate_vouchers);
                    }
                    if (updates.linked_offer_id !== undefined) {
                      setLinkedOfferId(updates.linked_offer_id);
                    }
                    if (updates.evaluation_interval_minutes !== undefined) {
                      setEvaluationIntervalMinutes(
                        updates.evaluation_interval_minutes,
                      );
                    }
                    if (updates.is_active !== undefined) {
                      setIsActive(updates.is_active);
                    }
                  }}
                />
              </div>
            )}

            {/* Static list info */}
            {!isDynamic && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-yellow-800">
                  Static lists are manually curated. After creating this list,
                  you can add contacts from the Contacts page or individual
                  contact details.
                </p>
              </div>
            )}

            {/* Tag Actions */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Tag Actions</h3>
              <p className="text-xs text-gray-500 -mt-2">
                Automatically apply or remove a tag when contacts enter or exit
                this segment.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <TagAutocompleteInput
                  id="entry-tag"
                  label="Apply tag on entry"
                  value={entryTag}
                  onChange={setEntryTag}
                  placeholder="e.g. in_segment"
                  helpText="Added when a contact matches this segment"
                />
                <TagAutocompleteInput
                  id="exit-tag"
                  label="Remove tag on exit"
                  value={exitTag}
                  onChange={setExitTag}
                  placeholder="e.g. in_segment"
                  helpText="Removed when a contact no longer matches"
                />
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                "Save List"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
